import catalogData from './subscription_catalog.json';
import type {
  EntitlementId,
  MembershipPlanId,
  ProductId,
  SubscriptionCatalog,
  SubscriptionProduct,
} from './types';

export const SUBSCRIPTION_CATALOG = catalogData as SubscriptionCatalog;

export const SUBSCRIPTION_PRODUCTS: SubscriptionProduct[] = SUBSCRIPTION_CATALOG.products;

export const ALL_ENTITLEMENT_IDS: EntitlementId[] = SUBSCRIPTION_CATALOG.entitlements;

export const BASELINE_ENTITLEMENT_IDS: EntitlementId[] =
  SUBSCRIPTION_CATALOG.baseline_entitlements;

export const TRIAL_ENTITLEMENT_IDS: EntitlementId[] =
  SUBSCRIPTION_CATALOG.trial_entitlements;

export const ALL_PRODUCT_IDS: ProductId[] = SUBSCRIPTION_PRODUCTS.map((p) => p.id);

/** Plans an individual can buy, monthly first. */
export const MEMBERSHIP_PLAN_IDS: MembershipPlanId[] = SUBSCRIPTION_PRODUCTS.filter(
  (p) => p.purchasable,
).map((p) => p.id as MembershipPlanId);

export function getProduct(id: ProductId): SubscriptionProduct | undefined {
  return SUBSCRIPTION_PRODUCTS.find((p) => p.id === id);
}

/** Entitlements a product grants, including the baseline every product grants. */
export function productGrants(id: ProductId): EntitlementId[] {
  const product = getProduct(id);
  if (!product) {
    return [];
  }
  return Array.from(new Set([...BASELINE_ENTITLEMENT_IDS, ...product.grants]));
}

/** The slice a free trial unlocks; the rest lands on the first charge. */
export function productTrialGrants(id: ProductId): EntitlementId[] {
  const full = productGrants(id);
  return TRIAL_ENTITLEMENT_IDS.filter((entitlementId) => full.includes(entitlementId));
}

export function formatPriceCents(cents: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
  }).format(cents / 100);
}

/** "$29.99/month" — the price as it must be disclosed next to a buy button. */
export function formatPlanPrice(product: SubscriptionProduct): string {
  const price = formatPriceCents(product.price_cents);
  if (!product.billing_interval) {
    return price;
  }
  return `${price}/${product.billing_interval}`;
}
